<template>
  <settings-modal
    class="swift-message-create-modal t-swift-msg-create-modal"
    :locked="loading"
  >
    <template slot="heading">Please, check all information again and confirm</template>

    <scrollable-container slot="raw">
      <data-row>
        <v-flex>
          <one-data-column>
            <data-label slot="label">Message type</data-label>
            <div class="t-message-type">{{ messageData.messageType }} ({{ messageTypeName }})</div>
          </one-data-column>

          <template v-if="isBetweenCompanyAccounts">
            <one-data-column>
              <data-label slot="label">Sender</data-label>
              <div class="t-sender">{{ messageData.sender }} ({{ senderAccount.currency != null ? senderAccount.currency : 'Multi' }})</div>
            </one-data-column>

            <one-data-column>
              <data-label slot="label">Recipient</data-label>
              <div class="t-recipient">{{ messageData.recipient }} ({{ recipientAccount.currency != null ? recipientAccount.currency : 'Multi' }})</div>
            </one-data-column>

            <one-data-column v-if="messageData.institutionAccount">
              <data-label slot="label">Institution account</data-label>
              <div class="t-institution-account">{{ messageData.institutionAccount }}</div>
            </one-data-column>

            <one-data-column v-if="messageData.correspondentAccount">
              <data-label slot="label">Correspondent account</data-label>
              <div class="t-correspondent-account">{{ messageData.correspondentAccount }}</div>
            </one-data-column>

            <one-data-column v-if="messageData.correspondentBank">
              <data-label slot="label">Correspondent bank</data-label>
              <div class="t-correspondent-bank">{{ messageData.correspondentBank }}</div>
            </one-data-column>

            <one-data-column>
              <data-label slot="label">Amount</data-label>
              <div class="t-amount">{{ messageData.amount | number }} {{ messageData.currency }}</div>
            </one-data-column>
          </template>

          <template v-else>
            <one-data-column v-if="messageData.receiver">
              <data-label slot="label">Receiver</data-label>
              <div class="t-receiver">{{ messageData.receiver }}</div>
            </one-data-column>

            <one-data-column v-if="bankDetails">
              <data-label slot="label">Bank name</data-label>
              <div class="t-bank-name">{{ bankDetails.bankName }}</div>
            </one-data-column>

            <one-data-column v-if="messageData.transactionReferenceNumber">
              <data-label slot="label">Transaction reference Number</data-label>
              <div class="t-transaction-ref-number">{{ messageData.transactionReferenceNumber }}</div>
            </one-data-column>

            <one-data-column v-if="messageData.message">
              <data-label slot="label">{{ isQuery ? 'Queries' : 'Answers' }}</data-label>
              <div class="t-message">{{ messageData.message }}</div>
            </one-data-column>

            <one-data-column v-if="messageData.relatedReference">
              <data-label slot="label">Related reference</data-label>
              <div class="t-related-ref">{{ messageData.relatedReference }}</div>
            </one-data-column>
          </template>

          <one-data-column>
            <data-label slot="label">Narrative</data-label>
            <div class="t-narrative">{{ messageData.narrative }}</div>
          </one-data-column>

          <one-data-column v-if="messageData.narrativeDescription">
            <data-label slot="label">Narrative description</data-label>
            <div class="t-narrative-description">{{ messageData.narrativeDescription }}</div>
          </one-data-column>

        </v-flex>
      </data-row>
    </scrollable-container>

    <template slot="actions">
      <v-btn
        class="t-create-button"
        color="primary"
        flat="flat"
        :disabled="loading"
        @click="onConfirmButtonClick"
      >create message</v-btn>

      <v-btn
        class="t-close-button"
        color="primary"
        flat="flat"
        :disabled="loading"
        @click="closeModal"
      >cancel</v-btn>
    </template>
  </settings-modal>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  // Utils
  import { createSwiftMessageOrderListRoute } from '@/utils/routing/SwiftMessageOrders'

  // Data
  import swiftMessageTypes from '@/assets/data/swift/message-types'

  // Constants
  import { SWIFT_MESSAGE_TYPES } from '@/models/SwiftMessage'

  // Events
  import { EVENT_SWIFT_MESSAGE_ORDER_CREATE } from '@/events/swiftMessageOrders'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'

  // Components
  import SettingsModal from '@/components/modals/SettingsModal'
  import ScrollableContainer from '@/components/ui/ScrollableContainer'
  import DataRow from '@/components/ui/Data/DataRow'
  import OneDataColumn from '@/components/ui/Data/OneDataColumn'
  import DataLabel from '@/components/ui/Data/DataLabel'
  import SwiftMessageOrderApi from '@/services/Api/SwiftMessageOrderApi'

  export default {
    name: 'SwiftMessageOrderCreateModal',
    components: {
      SettingsModal,
      ScrollableContainer,
      DataRow,
      OneDataColumn,
      DataLabel
    },
    computed: {
      ...mapGetters('modal', ['modal']),
      isBetweenCompanyAccounts () {
        return SWIFT_MESSAGE_TYPES.BETWEEN_COMPANY_ACCOUNTS.includes(this.messageData.messageType)
      },
      isQuery () {
        return SWIFT_MESSAGE_TYPES.QUERIES.includes(this.messageData.messageType)
      },
      createSwiftMessageApiRequest () {
        const { createSwiftMessageOrder, createBetweenCompanyAccounts } = SwiftMessageOrderApi

        return new ApiRequest(this.isBetweenCompanyAccounts ? createBetweenCompanyAccounts : createSwiftMessageOrder)
      },
      messageData () {
        return this.modal.data.swiftMessage
      },
      messageTypeName () {
        return swiftMessageTypes[this.messageData.messageType].mtName
      },
      bankDetails () {
        return this.modal.data.receiverBankDetails
      },
      senderAccount () {
        return this.modal.data.senderAccount
      },
      recipientAccount () {
        return this.modal.data.recipientAccount
      },
      loading () {
        return this.createSwiftMessageApiRequest.loading
      },
      requestData () {
        return Object.entries(this.messageData).reduce((requestData, [key, value]) => {
          requestData[key] = value == null ? undefined : value

          return requestData
        }, {})
      }
    },
    methods: {
      ...mapActions('modal', ['showModal', 'closeModal']),
      onConfirmButtonClick () {
        this.createSwiftMessageApiRequest
          .execute(this.requestData)
          .then(this.showSuccessFlashModal)
          .catch(error => {
            this.$eventHub.$emit(EVENT_SWIFT_MESSAGE_ORDER_CREATE, error)
            this.showErrorFlashModal()
          })
      },
      showSuccessFlashModal () {
        this.showModal({
          name: 'SuccessFlashModal',
          data: {
            redirectPath: createSwiftMessageOrderListRoute(),
            message: 'Message has been successfully created.'
          }
        })
      },
      showErrorFlashModal () {
        this.showModal({
          name: 'ErrorFlashModal',
          data: {
            message: 'Message has not been created.'
          }
        })
      }
    }
  }
</script>
